import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  FieldLabel,
  Flex,
  Input,
  Select,
  SelectContent,
  SelectTrigger,
  Textarea,
} from '@optiaxiom/react';

import { CompanionRule, CreateRuleRequest, UpdateRuleRequest } from '@frontend/types/app-companion';

import { eventNameOptions, productTypes } from '../constants';

type RuleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (rule: CreateRuleRequest | UpdateRuleRequest) => void;
  onCancel?: () => void;
  agentId: string;
  editRule?: CompanionRule | null;
};

export function RuleDialog({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  agentId,
  editRule,
}: RuleDialogProps) {
  const isEditMode = !!editRule;

  const [formData, setFormData] = useState<CreateRuleRequest>({
    event_name: '',
    agent_id: agentId,
    description: '',
    trigger_pattern: '',
  });
  const [product, setProduct] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editRule) {
      // Extract product from trigger_pattern (e.g., "product<CMP>/..." -> "CMP")
      const productMatch = editRule.trigger_pattern?.match(/^product<([^>]+)>\/(.*)$/);
      const extractedProduct = productMatch?.[1] ?? '';
      const patternWithoutPrefix = productMatch?.[2] ?? editRule.trigger_pattern ?? '';

      setProduct(extractedProduct);
      setFormData({
        event_name: editRule.event_name,
        agent_id: editRule.agent_id,
        description: editRule.description || '',
        trigger_pattern: patternWithoutPrefix,
      });
    } else {
      setFormData({
        event_name: '',
        agent_id: agentId,
        description: '',
        trigger_pattern: '',
      });
      setProduct('');
    }
  }, [editRule, agentId]);

  const handleSubmit = async () => {
    if (!formData.event_name || !formData.trigger_pattern || !product) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepend product<productName>/ to trigger_pattern if not already present
      const triggerPattern = formData.trigger_pattern.startsWith('product<')
        ? formData.trigger_pattern
        : `product<${product}>/${formData.trigger_pattern}`;

      await onSubmit({
        ...formData,
        trigger_pattern: triggerPattern,
      });
      setFormData({
        event_name: '',
        agent_id: agentId,
        description: '',
        trigger_pattern: '',
      });
      setProduct('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create rule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel?.();
    }
    onOpenChange(open);
  };

  const isValid = formData.event_name && formData.trigger_pattern && product;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>{isEditMode ? 'Edit Rule' : 'Add Rule'}</DialogHeader>
        <DialogBody>
          <Flex flexDirection="column" gap="16">
            <Field>
              <FieldLabel required>Product</FieldLabel>
              <Select
                value={product}
                onValueChange={value => setProduct(value)}
                options={productTypes}
                required
              >
                <SelectTrigger placeholder="Select a product" />
                <SelectContent />
              </Select>
            </Field>

            <Field>
              <FieldLabel required>Event Name</FieldLabel>
              <Select
                value={formData.event_name}
                onValueChange={value => setFormData({ ...formData, event_name: value })}
                options={eventNameOptions}
                required
              >
                <SelectTrigger placeholder="Select an event template" />
                <SelectContent />
              </Select>
            </Field>

            <Field>
              <FieldLabel required>Trigger Pattern</FieldLabel>
              <Input
                placeholder="Enter trigger pattern"
                value={formData.trigger_pattern}
                onChange={e => setFormData({ ...formData, trigger_pattern: e.target.value })}
                required
              />
            </Field>

            <Field>
              <FieldLabel>Description</FieldLabel>
              <Textarea
                placeholder="Enter rule description (optional)"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </Field>
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Button appearance="subtle" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting
              ? isEditMode
                ? 'Updating...'
                : 'Creating...'
              : isEditMode
                ? 'Update Rule'
                : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
